import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ApiErrorSummary } from '@/components/ApiErrorSummary';
import { RoleForm } from '../components/RoleForm';
import { getApiErrorDetails, type ApiErrorDetails } from '@/lib/getApiErrorMessage';
import { roleService } from '../services';
import type { CreateRoleFormData } from '../schemas';

export function CreateRolePage() {
  usePageTitle('Create Role');
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ApiErrorDetails | null>(null);

  const handleSubmit = async (data: CreateRoleFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await roleService.createRole({
        name: data.name,
        roleCode: data.roleCode,
        description: data.description || undefined,
        isActive: data.isActive,
      });
      toast.success('Role created successfully.');
      navigate('/administration/authorization');
    } catch (err) {
      const errorDetails = getApiErrorDetails(err, 'Failed to create role');
      setSubmitError(errorDetails);
      toast.error(errorDetails.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/administration/authorization');
  };

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCancel}
        className="gap-1"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Authorization
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Create New Role</CardTitle>
          <CardDescription>
            Add a new role to the system. Fill in all required fields.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ApiErrorSummary error={submitError} />
          <RoleForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </CardContent>
      </Card>
    </div>
  );
}
